import React, { useState, useEffect } from 'react';
import './Storyteller.css';
import './Storyteller_scene_preview.css';

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

const Storyteller = () => {
    const [stage, setStage] = useState('topic'); // topic, research, story, scenes, images, scene_preview, video, metadata, preview
    const [sessionId, setSessionId] = useState(null);
    
    // Video Type Selection
    const [videoType, setVideoType] = useState('how_to'); // 'how_to', 'everyday', or 'news'
    
    // News subcategory selection
    const [newsCategory, setNewsCategory] = useState('general'); // general, financial, guardian, ai
    
    // Language selection
    const [language, setLanguage] = useState('English'); // Default to English
    
    // Stage 1: Topic Selection
    const [topicSuggestions, setTopicSuggestions] = useState([]);
    const [selectedTopic, setSelectedTopic] = useState('');
    const [customTopic, setCustomTopic] = useState('');
    const [isGeneratingTopics, setIsGeneratingTopics] = useState(false);
    
    // Stage 2: Research
    const [researchData, setResearchData] = useState(null);
    const [isResearching, setIsResearching] = useState(false);
    
    // Stage 3: Story
    const [storyData, setStoryData] = useState(null);
    const [isGeneratingStory, setIsGeneratingStory] = useState(false);
    
    // Stage 4: Scenes
    const [scenes, setScenes] = useState([]);
    const [isGeneratingScenes, setIsGeneratingScenes] = useState(false);
    
    // Image References
    const [styleReferenceFile, setStyleReferenceFile] = useState(null);
    const [characterReferenceFile, setCharacterReferenceFile] = useState(null);
    const [characterDescription, setCharacterDescription] = useState('');
    const [styleReferenceId, setStyleReferenceId] = useState(null);
    const [characterReferenceId, setCharacterReferenceId] = useState(null);
    const [isUploadingReferences, setIsUploadingReferences] = useState(false);
    
    // Stage 5: Images
    const [imageResults, setImageResults] = useState([]);
    const [isGeneratingImages, setIsGeneratingImages] = useState(false);
    const [imageProgress, setImageProgress] = useState({ completed: 0, total: 0 });
    const [currentSceneIndex, setCurrentSceneIndex] = useState(0);
    const [regeneratingScenes, setRegeneratingScenes] = useState(new Set());
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [customRegeneratePrompt, setCustomRegeneratePrompt] = useState('');
    
    // Stage 6: Scene Clips (individual scene videos with narration/subtitles)
    const [sceneClips, setSceneClips] = useState([]);
    const [isGeneratingClips, setIsGeneratingClips] = useState(false);
    const [clipProgress, setClipProgress] = useState({ completed: 0, total: 0 });
    const [currentClipIndex, setCurrentClipIndex] = useState(0);
    const [regeneratingClip, setRegeneratingClip] = useState(null);
    const [editingNarration, setEditingNarration] = useState(null);
    const [editedNarrationText, setEditedNarrationText] = useState('');
    
    // Stage 7: Final Video Assembly
    const [videoPath, setVideoPath] = useState(null);
    const [isAssemblingVideo, setIsAssemblingVideo] = useState(false);
    const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);
    const [videoStatus, setVideoStatus] = useState('');
    
    // Stage 8: Metadata
    const [metadata, setMetadata] = useState(null);
    const [isGeneratingMetadata, setIsGeneratingMetadata] = useState(false);
    
    // Stage 8: Upload
    const [youtubeUrl, setYoutubeUrl] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    
    useEffect(() => {
        // Generate a session ID on mount
        setSessionId(`story_${Date.now()}`);
    }, []);

    // Poll for image generation status (generation is triggered by generateImages function)
    useEffect(() => {
        let pollStatus;
        if (isGeneratingImages && sessionId) {
            // Only poll for status - generation is already started by generateImages()
            pollStatus = setInterval(async () => {
                try {
                    const statusRes = await fetch(`${API_URL}/storyteller/images/status/${sessionId}`);
                    const statusData = await statusRes.json();
                    
                    const completed = statusData.completed || 0;
                    const total = statusData.total || scenes.length;
                    
                    setImageProgress({ completed, total });
                    
                    // Only show images when ALL are complete
                    if (statusData.status === 'complete' && completed === total) {
                        clearInterval(pollStatus);
                        setImageResults(statusData.images || []);
                        setIsGeneratingImages(false);
                        setStage('images');
                        
                        // Save session with images
                        await saveSession(selectedTopic || customTopic, researchData, storyData, scenes, statusData.images);
                    }
                } catch (err) {
                    console.error("Error polling image status:", err);
                }
            }, 2000);
        }
        return () => {
            if (pollStatus) clearInterval(pollStatus);
        };
    }, [isGeneratingImages, sessionId]);

    // Poll for video generation status
    useEffect(() => {
        let interval;
        if (isGeneratingVideo && sessionId) {
            interval = setInterval(async () => {
                try {
                    const res = await fetch(`${API_URL}/storyteller/video/status/${sessionId}`);
                    const data = await res.json();
                    
                    setVideoStatus(data.status);
                    
                    if (data.video_ready) {
                        setVideoPath(data.video_path);
                        setIsGeneratingVideo(false);
                    }
                } catch (err) {
                    console.error("Error checking video status:", err);
                }
            }, 3000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [isGeneratingVideo, sessionId]);

    // Poll for scene clips generation status
    useEffect(() => {
        let pollStatus;
        if (isGeneratingClips && sessionId) {
            pollStatus = setInterval(async () => {
                try {
                    const res = await fetch(`${API_URL}/storyteller/scene-clips/status/${sessionId}`);
                    const data = await res.json();
                    
                    setClipProgress({ completed: data.completed || 0, total: data.total || 0 });
                    
                    if (data.status === 'complete') {
                        clearInterval(pollStatus);
                        setSceneClips(data.clips || []);
                        setIsGeneratingClips(false);
                        setStage('scene_preview');
                    }
                } catch (err) {
                    console.error("Error polling scene clips status:", err);
                }
            }, 2000);
        }
        return () => {
            if (pollStatus) clearInterval(pollStatus);
        };
    }, [isGeneratingClips, sessionId]);

    const generateTopicSuggestions = async () => {
        setIsGeneratingTopics(true);
        try {
            const res = await fetch(`${API_URL}/storyteller/topics/generate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ count: 5, video_type: videoType })
            });
            const data = await res.json();
            setTopicSuggestions(data.topics || []);
        } catch (err) {
            console.error("Error generating topics:", err);
            alert("Failed to generate topics");
        } finally {
            setIsGeneratingTopics(false);
        }
    };

    const handleTopicSelect = (topic) => {
        setSelectedTopic(topic);
        setCustomTopic('');
    };

    const proceedToResearch = async () => {
        // For news type, fetch news and skip research - go directly to story generation
        if (videoType === 'news') {
            setIsResearching(true);
            try {
                const res = await fetch(`${API_URL}/storyteller/news/fetch`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ category: newsCategory })
                });
                const data = await res.json();
                
                if (data.error) {
                    alert(data.error);
                    setIsResearching(false);
                    return;
                }
                
                // Store news data as research (for reference)
                const newsResearch = data.research;
                setResearchData(newsResearch);
                setSelectedTopic(newsResearch?.title || `${newsCategory} News`);
                
                // Skip research stage - directly generate story from news
                setIsResearching(false);
                setIsGeneratingStory(true);
                
                const storyRes = await fetch(`${API_URL}/storyteller/story/generate`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ research_data: newsResearch, video_type: 'news', language })
                });
                const storyResult = await storyRes.json();
                console.log("Story generation result:", storyResult);
                
                if (!storyResult.story || Object.keys(storyResult.story).length === 0) {
                    alert("Story generation returned empty. Check backend logs.");
                    setIsGeneratingStory(false);
                    return;
                }
                
                setStoryData(storyResult.story);
                setIsGeneratingStory(false);
                
                // Skip to story stage (skipping research display)
                setStage('story');
                
                // Save session
                await saveSession(newsResearch?.title || `${newsCategory} News`, newsResearch, storyResult.story, [], {});
            } catch (err) {
                console.error("Error fetching news:", err);
                alert("Failed to fetch news. Check API keys in .env file.");
                setIsResearching(false);
                setIsGeneratingStory(false);
            }
            return;
        }
        
        const topic = customTopic || selectedTopic;
        if (!topic) {
            alert("Please select or enter a topic");
            return;
        }
        
        setIsResearching(true);
        try {
            const res = await fetch(`${API_URL}/storyteller/research`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ topic, video_type: videoType })
            });
            const data = await res.json();
            setResearchData(data.research);
            setStage('research');
            
            // Save session
            await saveSession(topic, data.research, null, [], {});
        } catch (err) {
            console.error("Error conducting research:", err);
            alert("Failed to conduct research");
        } finally {
            setIsResearching(false);
        }
    };

    const generateStory = async () => {
        setIsGeneratingStory(true);
        try {
            const res = await fetch(`${API_URL}/storyteller/story/generate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ research_data: researchData, video_type: videoType, language })
            });
            const data = await res.json();
            setStoryData(data.story);
            setStage('story');
            
            // Save session
            await saveSession(selectedTopic || customTopic, researchData, data.story, [], {});
        } catch (err) {
            console.error("Error generating story:", err);
            alert("Failed to generate story");
        } finally {
            setIsGeneratingStory(false);
        }
    };

    const generateScenes = async () => {
        setIsGeneratingScenes(true);
        try {
            // Pass reference_images from research phase to scene generation
            const referenceImages = researchData?.reference_images || [];
            const res = await fetch(`${API_URL}/storyteller/scenes/generate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    story_data: storyData, 
                    video_type: videoType, 
                    language,
                    reference_images: referenceImages
                })
            });
            const data = await res.json();
            setScenes(data.scenes || []);
            setStage('scenes');
            
            // Save session
            await saveSession(selectedTopic || customTopic, researchData, storyData, data.scenes, {});
        } catch (err) {
            console.error("Error generating scenes:", err);
            alert("Failed to generate scenes");
        } finally {
            setIsGeneratingScenes(false);
        }
    };

    const uploadReferenceImage = async (file, refType) => {
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('ref_type', refType);
            
            const res = await fetch(`${API_URL}/storyteller/references/upload?ref_type=${refType}`, {
                method: 'POST',
                body: formData
            });
            
            const data = await res.json();
            
            if (data.status === 'success') {
                if (refType === 'style') {
                    setStyleReferenceId(data.image_id);
                } else {
                    setCharacterReferenceId(data.image_id);
                }
                return data.image_id;
            }
            return null;
        } catch (err) {
            console.error(`Error uploading ${refType} reference:`, err);
            return null;
        }
    };

    const handleUploadReferences = async () => {
        setIsUploadingReferences(true);
        
        try {
            // Upload style reference if provided
            if (styleReferenceFile) {
                console.log('📤 Uploading style reference...');
                const styleId = await uploadReferenceImage(styleReferenceFile, 'style');
                if (styleId) {
                    console.log('✅ Style reference uploaded:', styleId);
                } else {
                    alert('Failed to upload style reference');
                }
            }
            
            // Upload character reference if provided
            if (characterReferenceFile) {
                console.log('📤 Uploading character reference...');
                const charId = await uploadReferenceImage(characterReferenceFile, 'character');
                if (charId) {
                    console.log('✅ Character reference uploaded:', charId);
                } else {
                    alert('Failed to upload character reference');
                }
            }
            
            // Generate character sheet if description provided but no file
            if (characterDescription && !characterReferenceFile) {
                console.log('🎭 Generating character sheet...');
                const res = await fetch(`${API_URL}/storyteller/character/generate`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        character_description: characterDescription,
                        session_id: sessionId
                    })
                });
                const data = await res.json();
                if (data.status === 'success') {
                    setCharacterReferenceId(data.character_id);
                    console.log('✅ Character sheet generated:', data.character_id);
                } else {
                    alert('Failed to generate character sheet');
                }
            }
            
            alert('References uploaded successfully!');
        } catch (err) {
            console.error('Error uploading references:', err);
            alert('Failed to upload references');
        } finally {
            setIsUploadingReferences(false);
        }
    };

    const generateImages = async () => {
        console.log('🎨 Generate Images clicked');
        console.log('Session ID:', sessionId);
        console.log('Scenes:', scenes);
        console.log('Scenes length:', scenes?.length);
        console.log('Character Description:', characterDescription);
        
        if (!scenes || scenes.length === 0) {
            alert("No scenes available. Please generate scenes first.");
            return;
        }
        
        setIsGeneratingImages(true);
        setImageProgress({ completed: 0, total: scenes.length });
        
        try {
            // Include reference_images from research phase for style transfer
            const referenceImages = researchData?.reference_images || [];
            const payload = { 
                session_id: sessionId, 
                scenes,
                character_description: characterDescription || null,
                reference_images: referenceImages
            };
            console.log('Sending payload:', payload);
            console.log('Reference images:', referenceImages);
            
            const res = await fetch(`${API_URL}/storyteller/images/generate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            
            console.log('Response:', data);
            
            if (data.status === 'started') {
                setStage('images');
            }
        } catch (err) {
            console.error("Error generating images:", err);
            alert("Failed to start image generation");
            setIsGeneratingImages(false);
        }
    };

    const generateVideo = async () => {
        console.log('🎬 Generate Video clicked');
        console.log('Session ID:', sessionId);
        console.log('Scenes:', scenes);
        console.log('Image Results:', imageResults);
        
        if (!scenes || scenes.length === 0) {
            alert("No scenes available. Please generate scenes first.");
            return;
        }
        
        if (!imageResults || imageResults.length === 0) {
            alert("No images available. Please generate images first.");
            return;
        }
        
        setIsGeneratingVideo(true);
        setVideoStatus('starting');
        
        try {
            const payload = {
                session_id: sessionId,
                scenes: scenes,
                images: imageResults
            };
            console.log('Sending video payload:', payload);
            
            const res = await fetch(`${API_URL}/storyteller/video/generate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            
            console.log('Video response:', data);
            
            if (data.status === 'started') {
                setStage('video');
            }
        } catch (err) {
            console.error("Error generating video:", err);
            alert("Failed to start video generation");
            setIsGeneratingVideo(false);
        }
    };

    // Generate scene clips (individual videos with narration/subtitles)
    const generateSceneClips = async () => {
        if (!imageResults || imageResults.length === 0) {
            alert("No images available for video generation");
            return;
        }
        
        setIsGeneratingClips(true);
        setClipProgress({ completed: 0, total: scenes.length });
        
        try {
            const payload = {
                session_id: sessionId,
                scenes: scenes,
                images: imageResults
            };
            
            const res = await fetch(`${API_URL}/storyteller/scene-clips/generate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            
            if (data.status === 'started') {
                // Polling will handle the rest
                console.log('Scene clips generation started');
            }
        } catch (err) {
            console.error("Error generating scene clips:", err);
            alert("Failed to start scene clip generation");
            setIsGeneratingClips(false);
        }
    };

    // Regenerate a single scene's narration and subtitles
    const regenerateSceneClip = async (clipIndex) => {
        const clip = sceneClips[clipIndex];
        if (!clip) return;
        
        const scene = scenes.find(s => s.scene_number === clip.scene_number);
        if (!scene) return;
        
        setRegeneratingClip(clipIndex);
        
        try {
            const payload = {
                session_id: sessionId,
                scene_number: clip.scene_number,
                scene: scene,
                image_path: clip.image_path,
                new_narration: editingNarration === clipIndex ? editedNarrationText : null
            };
            
            const res = await fetch(`${API_URL}/storyteller/scene-clips/regenerate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            
            if (data.status === 'success' && data.clip) {
                // Update the clip in state
                const newClips = [...sceneClips];
                newClips[clipIndex] = data.clip;
                setSceneClips(newClips);
                
                // Also update the scene's narration if it was edited
                if (editingNarration === clipIndex && editedNarrationText) {
                    const newScenes = [...scenes];
                    const sceneIndex = newScenes.findIndex(s => s.scene_number === clip.scene_number);
                    if (sceneIndex >= 0) {
                        newScenes[sceneIndex] = { ...newScenes[sceneIndex], narration_text: editedNarrationText };
                        setScenes(newScenes);
                    }
                }
                
                setEditingNarration(null);
                setEditedNarrationText('');
            }
        } catch (err) {
            console.error("Error regenerating scene clip:", err);
            alert("Failed to regenerate scene clip");
        } finally {
            setRegeneratingClip(null);
        }
    };

    // Assemble final video with background music
    const assembleFinalVideo = async () => {
        if (!sceneClips || sceneClips.length === 0) {
            alert("No scene clips available for assembly");
            return;
        }
        
        setIsAssemblingVideo(true);
        setVideoStatus('assembling');
        
        try {
            const payload = {
                session_id: sessionId,
                scene_clips: sceneClips
            };
            
            const res = await fetch(`${API_URL}/storyteller/video/assemble`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            
            if (data.status === 'started') {
                setStage('video');
                setIsGeneratingVideo(true);
                setIsAssemblingVideo(false);
            }
        } catch (err) {
            console.error("Error assembling video:", err);
            alert("Failed to start video assembly");
            setIsAssemblingVideo(false);
        }
    };

    const generateMetadata = async () => {
        setIsGeneratingMetadata(true);
        try {
            const res = await fetch(`${API_URL}/storyteller/metadata/generate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ story_data: storyData })
            });
            const data = await res.json();
            setMetadata(data.metadata);
            setStage('metadata');
            
            // Save session
            await saveSession(selectedTopic || customTopic, researchData, storyData, scenes, data.metadata);
        } catch (err) {
            console.error("Error generating metadata:", err);
            alert("Failed to generate metadata");
        } finally {
            setIsGeneratingMetadata(false);
        }
    };

    const uploadToYouTube = async () => {
        setIsUploading(true);
        try {
            const res = await fetch(`${API_URL}/storyteller/upload`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ session_id: sessionId })
            });
            const data = await res.json();
            
            if (data.youtube_url) {
                setYoutubeUrl(data.youtube_url);
                setStage('preview');
            } else {
                alert("Upload failed");
            }
        } catch (err) {
            console.error("Error uploading to YouTube:", err);
            alert("Failed to upload to YouTube");
        } finally {
            setIsUploading(false);
        }
    };

    const saveSession = async (topic, research, story, scenesList, meta) => {
        try {
            await fetch(`${API_URL}/storyteller/session/save`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    session_id: sessionId,
                    topic,
                    research_data: research,
                    story_data: story,
                    scenes: scenesList,
                    metadata: meta
                })
            });
        } catch (err) {
            console.error("Error saving session:", err);
        }
    };

    const regenerateScene = async (sceneIndex, customPrompt = '') => {
        const newRegenerating = new Set(regeneratingScenes);
        newRegenerating.add(sceneIndex);
        setRegeneratingScenes(newRegenerating);
        
        try {
            const scene = scenes[sceneIndex];
            const res = await fetch(`${API_URL}/storyteller/images/regenerate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    session_id: sessionId,
                    scene_number: scene.scene_number,
                    scene: scene,
                    custom_prompt: customPrompt
                })
            });
            
            if (!res.ok) {
                const errorData = await res.json();
                throw new Error(errorData.detail || 'Failed to regenerate');
            }
            
            const data = await res.json();
            
            if (data.image) {
                // Update the image result for this scene
                const updatedResults = [...imageResults];
                updatedResults[sceneIndex] = data.image;
                setImageResults(updatedResults);
                // Clear custom prompt after successful regeneration
                setCustomRegeneratePrompt('');
            }
        } catch (err) {
            console.error("Error regenerating scene:", err);
            alert(`Failed to regenerate scene: ${err.message}`);
        } finally {
            const newRegenerating = new Set(regeneratingScenes);
            newRegenerating.delete(sceneIndex);
            setRegeneratingScenes(newRegenerating);
        }
    };

    const resetWorkflow = () => {
        if (!window.confirm('Are you sure you want to start over? All progress will be lost.')) {
            return;
        }
        setStage('topic');
        setSessionId(`story_${Date.now()}`);
        setTopicSuggestions([]);
        setSelectedTopic('');
        setCustomTopic('');
        setResearchData(null);
        setStoryData(null);
        setScenes([]);
        setImageResults([]);
        setVideoPath(null);
        setMetadata(null);
        setYoutubeUrl(null);
    };

    const goBackToStage = (targetStage) => {
        setStage(targetStage);
    };

    const getNextButtonInfo = () => {
        switch(stage) {
            case 'topic': return { text: 'Proceed to Research →', onClick: proceedToResearch, disabled: isResearching || (videoType !== 'news' && !customTopic && !selectedTopic) };
            case 'research': return { text: 'Generate Story →', onClick: generateStory, disabled: isGeneratingStory };
            case 'story': return { text: 'Break into Scenes →', onClick: generateScenes, disabled: isGeneratingScenes };
            case 'scenes': return { text: 'Generate Images →', onClick: generateImages, disabled: isGeneratingImages || !scenes || scenes.length === 0 };
            case 'images': return imageResults.length > 0 ? { text: 'Create Scene Clips →', onClick: generateSceneClips, disabled: isGeneratingClips } : null;
            case 'scene_preview': return null; // Assemble button is in the scene preview UI
            case 'video': return videoPath ? { text: 'Generate Metadata →', onClick: generateMetadata, disabled: isGeneratingMetadata } : null;
            case 'metadata': return { text: 'Upload to YouTube →', onClick: uploadToYouTube, disabled: isUploading };
            case 'preview': return { text: 'Create Another Story', onClick: resetWorkflow, disabled: false };
            default: return null;
        }
    };

    const getPreviousStage = () => {
        switch(stage) {
            case 'research': return 'topic';
            case 'story': return 'research';
            case 'scenes': return 'story';
            case 'images': return 'scenes';
            case 'scene_preview': return 'images';
            case 'video': return 'scene_preview';
            case 'metadata': return 'video';
            case 'preview': return 'metadata';
            default: return null;
        }
    };

    const nextButton = getNextButtonInfo();
    const previousStage = getPreviousStage();

    return (
        <div className="storyteller-wrapper">
            <div className="storyteller-header">
                <div className="header-left">
                    {previousStage && (
                        <button className="header-nav-btn secondary" onClick={() => goBackToStage(previousStage)}>
                            ← Back
                        </button>
                    )}
                    {stage !== 'topic' && (
                        <button className="header-nav-btn danger" onClick={resetWorkflow}>
                            🔄 Reset
                        </button>
                    )}
                </div>
                
                <div className="header-center">
                    <h1>📖 Storyteller</h1>
                    <p>AI-powered story video generation with narration and visuals</p>
                </div>
                
                <div className="header-right">
                    {nextButton && (
                        <button 
                            className="header-nav-btn primary" 
                            onClick={nextButton.onClick}
                            disabled={nextButton.disabled}
                        >
                            {nextButton.text}
                        </button>
                    )}
                </div>
            </div>
            

            {/* Progress Steps */}
            <div className="progress-steps">
                {['topic', 'research', 'story', 'scenes', 'images', 'scene_preview', 'video', 'metadata', 'preview'].map((s, idx) => (
                    <div 
                        key={s} 
                        className={`step ${stage === s ? 'active' : ''} ${
                            ['topic', 'research', 'story', 'scenes', 'images', 'scene_preview', 'video', 'metadata', 'preview'].indexOf(stage) > idx ? 'completed' : ''
                        }`}
                    >
                        <div className="step-number">{idx + 1}</div>
                        <div className="step-label">{s === 'scene_preview' ? 'Clips' : s}</div>
                    </div>
                ))}
            </div>

            {/* Stage 1: Topic Selection */}
            {stage === 'topic' && (
                <div className="stage-content">
                    <div className="stage-content-inner">
                        <h2>Step 1: Select Video Type & Topic</h2>
                        
                        {/* Video Type Selection */}
                        <div className="video-type-selector" style={{marginBottom: '30px'}}>
                        <h3 style={{color: '#888', fontSize: '1rem', marginBottom: '15px'}}>Choose Video Type:</h3>
                        <div style={{display: 'flex', gap: '15px'}}>
                            <button
                                onClick={() => setVideoType('how_to')}
                                className={videoType === 'how_to' ? 'type-btn active' : 'type-btn'}
                                style={{
                                    flex: 1,
                                    padding: '20px',
                                    background: videoType === 'how_to' ? '#667eea' : '#1f1f1f',
                                    border: videoType === 'how_to' ? '2px solid #667eea' : '2px solid #333',
                                    borderRadius: '12px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                            >
                                <div style={{fontSize: '2rem', marginBottom: '10px'}}>🎓</div>
                                <div style={{fontWeight: 'bold', marginBottom: '8px'}}>How-To / Educational</div>
                                <div style={{fontSize: '0.85rem', color: '#aaa'}}>
                                    Deep research with books, techniques, and real-world examples
                                </div>
                            </button>
                            <button
                                onClick={() => setVideoType('everyday')}
                                className={videoType === 'everyday' ? 'type-btn active' : 'type-btn'}
                                style={{
                                    flex: 1,
                                    padding: '20px',
                                    background: videoType === 'everyday' ? '#667eea' : '#1f1f1f',
                                    border: videoType === 'everyday' ? '2px solid #667eea' : '2px solid #333',
                                    borderRadius: '12px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                            >
                                <div style={{fontSize: '2rem', marginBottom: '10px'}}>😊</div>
                                <div style={{fontWeight: 'bold', marginBottom: '8px'}}>Everyday Story</div>
                                <div style={{fontSize: '0.85rem', color: '#aaa'}}>
                                    Relatable situations that everyone can connect with
                                </div>
                            </button>
                            <button
                                onClick={() => setVideoType('news')}
                                className={videoType === 'news' ? 'type-btn active' : 'type-btn'}
                                style={{
                                    flex: 1,
                                    padding: '20px',
                                    background: videoType === 'news' ? '#667eea' : '#1f1f1f',
                                    border: videoType === 'news' ? '2px solid #667eea' : '2px solid #333',
                                    borderRadius: '12px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                            >
                                <div style={{fontSize: '2rem', marginBottom: '10px'}}>📰</div>
                                <div style={{fontWeight: 'bold', marginBottom: '8px'}}>News Analysis</div>
                                <div style={{fontSize: '0.85rem', color: '#aaa'}}>
                                    Auto-fetch today's news and create analysis videos
                                </div>
                            </button>
                            <button
                                onClick={() => setVideoType('educational')}
                                className={videoType === 'educational' ? 'type-btn active' : 'type-btn'}
                                style={{
                                    flex: 1,
                                    padding: '20px',
                                    background: videoType === 'educational' ? '#667eea' : '#1f1f1f',
                                    border: videoType === 'educational' ? '2px solid #667eea' : '2px solid #333',
                                    borderRadius: '12px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                            >
                                <div style={{fontSize: '2rem', marginBottom: '10px'}}>📐</div>
                                <div style={{fontWeight: 'bold', marginBottom: '8px'}}>Educational Explainer</div>
                                <div style={{fontSize: '0.85rem', color: '#aaa'}}>
                                    Step-by-step explanations with real-world examples
                                </div>
                            </button>
                            <button
                                onClick={() => setVideoType('health')}
                                className={videoType === 'health' ? 'type-btn active' : 'type-btn'}
                                style={{
                                    flex: 1,
                                    padding: '20px',
                                    background: videoType === 'health' ? '#667eea' : '#1f1f1f',
                                    border: videoType === 'health' ? '2px solid #667eea' : '2px solid #333',
                                    borderRadius: '12px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                            >
                                <div style={{fontSize: '2rem', marginBottom: '10px'}}>🏥</div>
                                <div style={{fontWeight: 'bold', marginBottom: '8px'}}>Health Education</div>
                                <div style={{fontSize: '0.85rem', color: '#aaa'}}>
                                    Evidence-based health topics with disclaimers
                                </div>
                            </button>
                            <button
                                onClick={() => setVideoType('custom')}
                                className={videoType === 'custom' ? 'type-btn active' : 'type-btn'}
                                style={{
                                    flex: 1,
                                    padding: '20px',
                                    background: videoType === 'custom' ? '#667eea' : '#1f1f1f',
                                    border: videoType === 'custom' ? '2px solid #667eea' : '2px solid #333',
                                    borderRadius: '12px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                            >
                                <div style={{fontSize: '2rem', marginBottom: '10px'}}>✨</div>
                                <div style={{fontWeight: 'bold', marginBottom: '8px'}}>Custom / Free Topic</div>
                                <div style={{fontSize: '0.85rem', color: '#aaa'}}>
                                    Product reviews, explainers, comparisons, anything!
                                </div>
                            </button>
                        </div>
                    </div>
                    
                    {/* Language Selection */}
                    <div className="language-selector" style={{marginBottom: '30px'}}>
                        <h3 style={{color: '#888', fontSize: '1rem', marginBottom: '15px'}}>Video Language:</h3>
                        <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap'}}>
                            {['English', 'Korean', 'Spanish', 'Japanese', 'Chinese', 'French', 'German'].map((lang) => (
                                <button
                                    key={lang}
                                    onClick={() => setLanguage(lang)}
                                    style={{
                                        padding: '10px 20px',
                                        background: language === lang ? '#667eea' : '#1f1f1f',
                                        border: language === lang ? '2px solid #667eea' : '2px solid #333',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        fontSize: '0.9rem',
                                        fontWeight: language === lang ? 'bold' : 'normal',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    {lang}
                                </button>
                            ))}
                        </div>
                    </div>
                    
                    {/* News Category Selection - only show when news type is selected */}
                    {videoType === 'news' && (
                        <div className="news-category-selector" style={{marginBottom: '30px'}}>
                            <h3 style={{color: '#888', fontSize: '1rem', marginBottom: '15px'}}>Select News Category:</h3>
                            <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px'}}>
                                <button
                                    onClick={() => setNewsCategory('general')}
                                    style={{
                                        padding: '15px',
                                        background: newsCategory === 'general' ? '#4a9eff' : '#1f1f1f',
                                        border: newsCategory === 'general' ? '2px solid #4a9eff' : '2px solid #333',
                                        borderRadius: '10px',
                                        cursor: 'pointer',
                                        textAlign: 'left'
                                    }}
                                >
                                    <div style={{fontSize: '1.5rem', marginBottom: '5px'}}>🌍</div>
                                    <div style={{fontWeight: 'bold', fontSize: '0.9rem'}}>General News</div>
                                    <div style={{fontSize: '0.75rem', color: '#888'}}>Multi-domain daily headlines</div>
                                </button>
                                <button
                                    onClick={() => setNewsCategory('financial')}
                                    style={{
                                        padding: '15px',
                                        background: newsCategory === 'financial' ? '#4a9eff' : '#1f1f1f',
                                        border: newsCategory === 'financial' ? '2px solid #4a9eff' : '2px solid #333',
                                        borderRadius: '10px',
                                        cursor: 'pointer',
                                        textAlign: 'left'
                                    }}
                                >
                                    <div style={{fontSize: '1.5rem', marginBottom: '5px'}}>📈</div>
                                    <div style={{fontWeight: 'bold', fontSize: '0.9rem'}}>Financial News</div>
                                    <div style={{fontSize: '0.75rem', color: '#888'}}>Stock market & crypto</div>
                                </button>
                                <button
                                    onClick={() => setNewsCategory('guardian')}
                                    style={{
                                        padding: '15px',
                                        background: newsCategory === 'guardian' ? '#4a9eff' : '#1f1f1f',
                                        border: newsCategory === 'guardian' ? '2px solid #4a9eff' : '2px solid #333',
                                        borderRadius: '10px',
                                        cursor: 'pointer',
                                        textAlign: 'left'
                                    }}
                                >
                                    <div style={{fontSize: '1.5rem', marginBottom: '5px'}}>📰</div>
                                    <div style={{fontWeight: 'bold', fontSize: '0.9rem'}}>The Guardian</div>
                                    <div style={{fontSize: '0.75rem', color: '#888'}}>Quality journalism</div>
                                </button>
                                <button
                                    onClick={() => setNewsCategory('ai')}
                                    style={{
                                        padding: '15px',
                                        background: newsCategory === 'ai' ? '#4a9eff' : '#1f1f1f',
                                        border: newsCategory === 'ai' ? '2px solid #4a9eff' : '2px solid #333',
                                        borderRadius: '10px',
                                        cursor: 'pointer',
                                        textAlign: 'left'
                                    }}
                                >
                                    <div style={{fontSize: '1.5rem', marginBottom: '5px'}}>🤖</div>
                                    <div style={{fontWeight: 'bold', fontSize: '0.9rem'}}>AI News</div>
                                    <div style={{fontSize: '0.75rem', color: '#888'}}>AI, LLMs, machine learning</div>
                                </button>
                            </div>
                        </div>
                    )}
                    
                    {/* Topic input - hide for news type since it auto-fetches */}
                    {videoType !== 'news' && (
                        <>
                            <div className="topic-input-section">
                                <input
                                    type="text"
                                    placeholder="Enter your own topic..."
                                    value={customTopic}
                                    onChange={(e) => {
                                        setCustomTopic(e.target.value);
                                        setSelectedTopic('');
                                    }}
                                    className="topic-input"
                                />
                                <button onClick={generateTopicSuggestions} disabled={isGeneratingTopics}>
                                    {isGeneratingTopics ? '🤖 Generating...' : '💡 Generate Suggestions'}
                                </button>
                            </div>

                            {topicSuggestions.length > 0 && (
                                <div className="topic-suggestions">
                                    <h3>AI Suggestions:</h3>
                                    {topicSuggestions.map((topic, idx) => (
                                        <div
                                            key={idx}
                                            className={`topic-card ${selectedTopic === topic ? 'selected' : ''}`}
                                            onClick={() => handleTopicSelect(topic)}
                                        >
                                            {topic}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                    
                    {/* News type info message */}
                    {videoType === 'news' && (
                        <div style={{
                            background: 'rgba(74, 158, 255, 0.1)',
                            border: '1px solid rgba(74, 158, 255, 0.3)',
                            borderRadius: '10px',
                            padding: '20px',
                            textAlign: 'center'
                        }}>
                            <div style={{fontSize: '2rem', marginBottom: '10px'}}>🚀</div>
                            <div style={{fontWeight: 'bold', marginBottom: '8px'}}>Ready to Generate News Video</div>
                            <div style={{fontSize: '0.9rem', color: '#888'}}>
                                Click "Proceed to Research" to automatically fetch today's {newsCategory} news and generate a detailed analysis video.
                            </div>
                        </div>
                    )}
                    </div>
                </div>
            )}

            {/* Stage 2: Research */}
            {stage === 'research' && researchData && (
                <div className="stage-content">
                    <h2>Step 2: Research Report</h2>
                    <div className="research-display">
                        <h3>{researchData.title}</h3>
                        
                        {/* How-To Video Research Display */}
                        {videoType === 'how_to' && (
                            <>
                                {/* Show overview if purpose not available */}
                                {(researchData.purpose || researchData.overview) && (
                                    <div className="research-section">
                                        <h4>🎯 Purpose & Overview</h4>
                                        <p>{researchData.purpose || researchData.overview}</p>
                                    </div>
                                )}
                                
                                {/* Show key facts as fallback */}
                                {researchData.key_facts && researchData.key_facts.length > 0 && (
                                    <div className="research-section">
                                        <h4>📌 Key Facts</h4>
                                        <ul>
                                            {researchData.key_facts.map((fact, idx) => (
                                                <li key={idx}>{fact}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                
                                {researchData.expert_sources && researchData.expert_sources.length > 0 && (
                                    <div className="research-section">
                                        <h4>📚 Expert Sources & Books</h4>
                                        {researchData.expert_sources.map((source, idx) => (
                                            <div key={idx} style={{marginBottom: '15px', padding: '12px', background: '#0a0a0a', borderRadius: '8px', borderLeft: '3px solid #667eea'}}>
                                                <div style={{fontWeight: 'bold', color: '#667eea', fontSize: '1.05rem'}}>
                                                    "{source.title}" by {source.author}
                                                    {source.year && <span style={{color: '#888', fontWeight: 'normal'}}> ({source.year})</span>}
                                                </div>
                                                {source.key_techniques && (
                                                    <div style={{marginTop: '10px', fontSize: '0.9rem'}}>
                                                        <strong style={{color: '#aaa'}}>Key Techniques:</strong>
                                                        <ul style={{marginTop: '5px', marginBottom: '0'}}>
                                                            {(Array.isArray(source.key_techniques) ? source.key_techniques : [source.key_techniques]).map((tech, i) => (
                                                                <li key={i} style={{color: '#ddd', marginTop: '3px'}}>{tech}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                                {source.advice && <div style={{marginTop: '8px', fontSize: '0.9rem', color: '#bbb', fontStyle: 'italic'}}>"{source.advice}"</div>}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {researchData.proven_techniques && researchData.proven_techniques.length > 0 && (
                                    <div className="research-section">
                                        <h4>✅ Proven Techniques</h4>
                                        <ul>
                                            {researchData.proven_techniques.map((tech, idx) => (
                                                <li key={idx}>
                                                    {typeof tech === 'string' ? tech : (
                                                        <>
                                                            <strong>{tech.technique}:</strong> {tech.description}
                                                            {tech.source && <span style={{color: '#888', fontSize: '0.85rem'}}> (Source: {tech.source})</span>}
                                                        </>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {researchData.common_mistakes && researchData.common_mistakes.length > 0 && (
                                    <div className="research-section">
                                        <h4>⚠️ Common Mistakes to Avoid</h4>
                                        <ul>
                                            {researchData.common_mistakes.map((mistake, idx) => (
                                                <li key={idx}>{mistake}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {researchData.step_by_step && researchData.step_by_step.length > 0 && (
                                    <div className="research-section">
                                        <h4>📝 Step-by-Step Guide</h4>
                                        <ol>
                                            {researchData.step_by_step.map((step, idx) => {
                                                // Handle different step formats
                                                if (typeof step === 'string') {
                                                    return step ? <li key={idx}>{step}</li> : null;
                                                } else if (step && typeof step === 'object') {
                                                    // Handle object with step/description
                                                    if (step.step || step.description) {
                                                        return (
                                                            <li key={idx}>
                                                                {step.step && <strong>{step.step}: </strong>}
                                                                {step.description && <span>{step.description}</span>}
                                                            </li>
                                                        );
                                                    }
                                                }
                                                return null;
                                            }).filter(Boolean)}
                                        </ol>
                                    </div>
                                )}
                                
                                {/* Show historical context and current relevance as additional info */}
                                {researchData.historical_context && (
                                    <div className="research-section">
                                        <h4>📜 Historical Context</h4>
                                        <p>{researchData.historical_context}</p>
                                    </div>
                                )}
                                
                                {researchData.current_relevance && (
                                    <div className="research-section">
                                        <h4>🌟 Current Relevance</h4>
                                        <p>{researchData.current_relevance}</p>
                                    </div>
                                )}
                                
                                {researchData.anecdotes && researchData.anecdotes.length > 0 && (
                                    <div className="research-section">
                                        <h4>💡 Real Examples</h4>
                                        <ul>
                                            {researchData.anecdotes.map((anecdote, idx) => (
                                                <li key={idx}>{anecdote}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </>
                        )}
                        
                        {/* Everyday Story Research Display */}
                        {videoType === 'everyday' && (
                            <>
                                {researchData.overview && (
                                    <div className="research-section">
                                        <h4>Overview</h4>
                                        <p>{researchData.overview}</p>
                                    </div>
                                )}
                                {researchData.relatability && (
                                    <div className="research-section">
                                        <h4>💭 Why It's Relatable</h4>
                                        <p>{researchData.relatability}</p>
                                    </div>
                                )}
                                {researchData.variations && researchData.variations.length > 0 && (
                                    <div className="research-section">
                                        <h4>🔄 Common Variations</h4>
                                        <ul>
                                            {researchData.variations.map((variation, idx) => (
                                                <li key={idx}>{variation}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {researchData.emotions && researchData.emotions.length > 0 && (
                                    <div className="research-section">
                                        <h4>❤️ Emotional Elements</h4>
                                        <p>{researchData.emotions.join(', ')}</p>
                                    </div>
                                )}
                            </>
                        )}
                        
                        {/* Custom / Free Topic Research Display */}
                        {videoType === 'custom' && (
                            <>
                                {researchData.overview && (
                                    <div className="research-section">
                                        <h4>📋 Overview</h4>
                                        <p>{researchData.overview}</p>
                                    </div>
                                )}
                                
                                {researchData.key_facts && researchData.key_facts.length > 0 && (
                                    <div className="research-section">
                                        <h4>📌 Key Facts</h4>
                                        <ul>
                                            {researchData.key_facts.map((fact, idx) => (
                                                <li key={idx}>{typeof fact === 'string' ? fact : JSON.stringify(fact)}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                
                                {researchData.history && (
                                    <div className="research-section">
                                        <h4>📜 History & Background</h4>
                                        <p>{researchData.history}</p>
                                    </div>
                                )}
                                
                                {researchData.how_it_works && (
                                    <div className="research-section">
                                        <h4>⚙️ How It Works</h4>
                                        <p>{researchData.how_it_works}</p>
                                    </div>
                                )}
                                
                                {researchData.use_cases && researchData.use_cases.length > 0 && (
                                    <div className="research-section">
                                        <h4>💡 Use Cases</h4>
                                        <ul>
                                            {researchData.use_cases.map((useCase, idx) => (
                                                <li key={idx}>{typeof useCase === 'string' ? useCase : JSON.stringify(useCase)}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                
                                {researchData.pricing && (
                                    <div className="research-section">
                                        <h4>💰 Pricing</h4>
                                        <p>{typeof researchData.pricing === 'string' ? researchData.pricing : JSON.stringify(researchData.pricing)}</p>
                                    </div>
                                )}
                                
                                {researchData.pros && researchData.pros.length > 0 && (
                                    <div className="research-section">
                                        <h4>✅ Pros</h4>
                                        <ul>
                                            {researchData.pros.map((pro, idx) => (
                                                <li key={idx} style={{color: '#4ade80'}}>{typeof pro === 'string' ? pro : JSON.stringify(pro)}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                
                                {researchData.cons && researchData.cons.length > 0 && (
                                    <div className="research-section">
                                        <h4>❌ Cons</h4>
                                        <ul>
                                            {researchData.cons.map((con, idx) => (
                                                <li key={idx} style={{color: '#f87171'}}>{typeof con === 'string' ? con : JSON.stringify(con)}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                
                                {researchData.competitors && researchData.competitors.length > 0 && (
                                    <div className="research-section">
                                        <h4>🏆 Competitors & Alternatives</h4>
                                        <ul>
                                            {researchData.competitors.map((comp, idx) => (
                                                <li key={idx}>{typeof comp === 'string' ? comp : JSON.stringify(comp)}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                
                                {researchData.expert_opinions && researchData.expert_opinions.length > 0 && (
                                    <div className="research-section">
                                        <h4>🎤 Expert Opinions</h4>
                                        <ul>
                                            {researchData.expert_opinions.map((opinion, idx) => (
                                                <li key={idx}>{typeof opinion === 'string' ? opinion : JSON.stringify(opinion)}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                
                                {researchData.recent_updates && (
                                    <div className="research-section">
                                        <h4>📰 Recent Updates</h4>
                                        <p>{typeof researchData.recent_updates === 'string' ? researchData.recent_updates : JSON.stringify(researchData.recent_updates)}</p>
                                    </div>
                                )}
                                
                                {researchData.verdict && (
                                    <div className="research-section" style={{background: 'rgba(102, 126, 234, 0.1)', padding: '15px', borderRadius: '8px', borderLeft: '3px solid #667eea'}}>
                                        <h4>🎯 Verdict</h4>
                                        <p style={{fontWeight: 'bold'}}>{researchData.verdict}</p>
                                    </div>
                                )}
                                
                                {researchData.practical_takeaways && researchData.practical_takeaways.length > 0 && (
                                    <div className="research-section">
                                        <h4>📝 Key Takeaways</h4>
                                        <ul>
                                            {researchData.practical_takeaways.map((takeaway, idx) => (
                                                <li key={idx}>{typeof takeaway === 'string' ? takeaway : JSON.stringify(takeaway)}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Stage 3: Story */}
            {stage === 'story' && storyData && (
                <div className="stage-content">
                    <h2>Step 3: Story</h2>
                    <div className="story-display">
                        <h3>{storyData.title || 'Untitled Story'}</h3>
                        {storyData.hook && <div className="story-hook">{storyData.hook}</div>}
                        <div className="story-body">
                            {typeof storyData.story === 'string' ? (
                                storyData.story
                            ) : Array.isArray(storyData.story) ? (
                                storyData.story.map((section, idx) => (
                                    <div key={idx} className="story-section">
                                        {typeof section === 'string' ? section : (
                                            <>
                                                {section.section && <h4>{section.section}</h4>}
                                                {section.script && <p>{section.script}</p>}
                                            </>
                                        )}
                                    </div>
                                ))
                            ) : storyData.story ? (
                                JSON.stringify(storyData.story)
                            ) : (
                                <p style={{color: '#888'}}>No story content generated. Try again.</p>
                            )}
                        </div>
                        {storyData.conclusion && <div className="story-conclusion">{storyData.conclusion}</div>}
                        {storyData.source_citation && (
                            <div className="story-source" style={{marginTop: '20px', padding: '10px', background: 'rgba(74, 158, 255, 0.1)', borderRadius: '8px'}}>
                                📰 Source: {storyData.source_citation}
                            </div>
                        )}
                        {storyData.estimated_duration_seconds && (
                            <p className="story-duration">Estimated Duration: {storyData.estimated_duration_seconds}s</p>
                        )}
                    </div>
                </div>
            )}

            {/* Stage 4: Scenes */}
            {stage === 'scenes' && scenes.length > 0 && (
                <div className="stage-content">
                    <h2>Step 4: Scenes ({scenes.length} scenes)</h2>
                    
                    <div className="scenes-list">
                        {scenes.map((scene, idx) => (
                            <div key={idx} className="scene-card">
                                <div className="scene-header">
                                    <span className="scene-number">Scene {scene.scene_number}</span>
                                    <span className="scene-duration">{scene.duration_seconds}s</span>
                                </div>
                                <div className="scene-narration">{scene.narration_text}</div>
                                <div className="scene-visual">🎨 {scene.visual_description}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Stage 5: Images */}
            {stage === 'images' && (
                <div className="stage-content">
                    <h2>Step 5: Image Generation</h2>
                    {isGeneratingClips ? (
                        <div className="loading-state">
                            <div className="spinner"></div>
                            <p>Creating scene clips with narration & subtitles...</p>
                            <p>{clipProgress.completed} / {clipProgress.total} scenes completed</p>
                        </div>
                    ) : isGeneratingImages && imageResults.length === 0 ? (
                        <div className="loading-state">
                            <div className="spinner"></div>
                            <p>Generating images with Gemini...</p>
                            <p>{imageProgress.completed} / {imageProgress.total} completed</p>
                        </div>
                    ) : imageResults.length > 0 ? (
                        <div className={`scene-preview-container ${isFullscreen ? 'fullscreen' : ''}`}>
                            <div className="scene-navigation">
                                <button 
                                    onClick={() => setCurrentSceneIndex(Math.max(0, currentSceneIndex - 1))}
                                    disabled={currentSceneIndex === 0}
                                    className="nav-button"
                                >
                                    ← Previous
                                </button>
                                <span className="scene-counter">
                                    Scene {currentSceneIndex + 1} of {imageResults.length}
                                </span>
                                <button 
                                    onClick={() => setIsFullscreen(!isFullscreen)}
                                    className="nav-button fullscreen-button"
                                    title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                                >
                                    {isFullscreen ? '⊗' : '⛶'}
                                </button>
                                <button 
                                    onClick={() => setCurrentSceneIndex(Math.min(imageResults.length - 1, currentSceneIndex + 1))}
                                    disabled={currentSceneIndex === imageResults.length - 1}
                                    className="nav-button"
                                >
                                    Next →
                                </button>
                            </div>
                            
                            {imageResults[currentSceneIndex] && (
                                <div className="scene-preview">
                                    <div className="scene-image-container">
                                        {regeneratingScenes.has(currentSceneIndex) ? (
                                            <div className="regenerating-overlay">
                                                <div className="spinner"></div>
                                                <p>Regenerating scene...</p>
                                            </div>
                                        ) : (
                                            <img 
                                                src={imageResults[currentSceneIndex].image_url} 
                                                alt={`Scene ${currentSceneIndex + 1}`}
                                                className="scene-preview-image"
                                            />
                                        )}
                                    </div>
                                    
                                    <div className="scene-details">
                                        <h3>Scene {currentSceneIndex + 1}</h3>
                                        <div className="scene-info">
                                            <p><strong>Narration:</strong></p>
                                            <p className="scene-narration">{scenes[currentSceneIndex]?.narration_text}</p>
                                            
                                            <p><strong>Visual Description:</strong></p>
                                            <p className="scene-visual">{scenes[currentSceneIndex]?.visual_description}</p>
                                            
                                            <p><strong>Mood:</strong> {scenes[currentSceneIndex]?.mood}</p>
                                            <p><strong>Duration:</strong> {scenes[currentSceneIndex]?.duration_seconds}s</p>
                                        </div>
                                        
                                        <div className="regenerate-section">
                                            <textarea
                                                placeholder="Custom prompt (optional) - describe what you want to see in this image..."
                                                value={customRegeneratePrompt}
                                                onChange={(e) => setCustomRegeneratePrompt(e.target.value)}
                                                className="custom-prompt-input"
                                                rows={3}
                                            />
                                            <div className="regenerate-buttons">
                                                <button 
                                                    onClick={() => regenerateScene(currentSceneIndex, customRegeneratePrompt)}
                                                    disabled={regeneratingScenes.has(currentSceneIndex)}
                                                    className="regenerate-button"
                                                >
                                                    {regeneratingScenes.has(currentSceneIndex) ? '⏳ Regenerating...' : '🔄 Regenerate Image'}
                                                </button>
                                                {customRegeneratePrompt && (
                                                    <button 
                                                        onClick={() => setCustomRegeneratePrompt('')}
                                                        className="clear-prompt-button"
                                                    >
                                                        ✕ Clear
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            <div className="scene-progress">
                                <div className="progress-dots">
                                    {imageResults.map((_, idx) => (
                                        <span 
                                            key={idx}
                                            className={`progress-dot ${idx === currentSceneIndex ? 'active' : ''}`}
                                            onClick={() => setCurrentSceneIndex(idx)}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>
            )}

            {/* Stage 6: Scene Preview */}
            {stage === 'scene_preview' && (
                <div className="stage-content">
                    <h2>Step 6: Scene Preview</h2>
                    <p style={{color: '#888', marginBottom: '20px'}}>
                        Review each scene with narration and subtitles. Edit narration or regenerate clips as needed.
                    </p>
                    
                    {sceneClips.length > 0 ? (
                        <div className="scene-preview-container">
                            {/* Main video player */}
                            <div className="scene-preview-main">
                                <div className="scene-preview-player">
                                    <video 
                                        key={sceneClips[currentClipIndex]?.video_url}
                                        controls 
                                        width="100%"
                                        autoPlay={false}
                                    >
                                        <source src={`${API_URL}${sceneClips[currentClipIndex]?.video_url}`} type="video/mp4" />
                                    </video>
                                </div>
                                
                                <div className="scene-preview-info">
                                    <h3>Scene {sceneClips[currentClipIndex]?.scene_number}</h3>
                                    
                                    {/* Narration editor */}
                                    <div className="narration-editor">
                                        <label>Narration:</label>
                                        {editingNarration === currentClipIndex ? (
                                            <textarea
                                                value={editedNarrationText}
                                                onChange={(e) => setEditedNarrationText(e.target.value)}
                                                rows={4}
                                                style={{width: '100%', marginTop: '8px'}}
                                            />
                                        ) : (
                                            <p style={{color: '#ccc', marginTop: '8px'}}>
                                                {sceneClips[currentClipIndex]?.narration_text}
                                            </p>
                                        )}
                                    </div>
                                    
                                    {/* Action buttons */}
                                    <div className="scene-preview-actions" style={{marginTop: '15px', display: 'flex', gap: '10px'}}>
                                        {editingNarration === currentClipIndex ? (
                                            <>
                                                <button 
                                                    className="btn-secondary"
                                                    onClick={() => {
                                                        setEditingNarration(null);
                                                        setEditedNarrationText('');
                                                    }}
                                                >
                                                    Cancel
                                                </button>
                                                <button 
                                                    className="btn-primary"
                                                    onClick={() => regenerateSceneClip(currentClipIndex)}
                                                    disabled={regeneratingClip !== null}
                                                >
                                                    {regeneratingClip === currentClipIndex ? 'Regenerating...' : 'Save & Regenerate'}
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button 
                                                    className="btn-secondary"
                                                    onClick={() => {
                                                        setEditingNarration(currentClipIndex);
                                                        setEditedNarrationText(sceneClips[currentClipIndex]?.narration_text || '');
                                                    }}
                                                >
                                                    ✏️ Edit Narration
                                                </button>
                                                <button 
                                                    className="btn-secondary"
                                                    onClick={() => regenerateSceneClip(currentClipIndex)}
                                                    disabled={regeneratingClip !== null}
                                                >
                                                    {regeneratingClip === currentClipIndex ? '🔄 Regenerating...' : '🔄 Regenerate Clip'}
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            {/* Scene navigation */}
                            <div className="scene-preview-nav" style={{marginTop: '20px'}}>
                                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px'}}>
                                    <button 
                                        className="btn-secondary"
                                        onClick={() => setCurrentClipIndex(Math.max(0, currentClipIndex - 1))}
                                        disabled={currentClipIndex === 0}
                                    >
                                        ← Previous
                                    </button>
                                    <span style={{color: '#888'}}>
                                        Scene {currentClipIndex + 1} of {sceneClips.length}
                                    </span>
                                    <button 
                                        className="btn-secondary"
                                        onClick={() => setCurrentClipIndex(Math.min(sceneClips.length - 1, currentClipIndex + 1))}
                                        disabled={currentClipIndex === sceneClips.length - 1}
                                    >
                                        Next →
                                    </button>
                                </div>
                                
                                {/* Thumbnail strip */}
                                <div className="scene-thumbnails" style={{display: 'flex', gap: '8px', overflowX: 'auto', padding: '10px 0'}}>
                                    {sceneClips.map((clip, idx) => (
                                        <div 
                                            key={clip.scene_number}
                                            className={`scene-thumb ${idx === currentClipIndex ? 'active' : ''}`}
                                            onClick={() => setCurrentClipIndex(idx)}
                                            style={{
                                                minWidth: '80px',
                                                height: '50px',
                                                background: idx === currentClipIndex ? '#6366f1' : '#333',
                                                borderRadius: '4px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                border: idx === currentClipIndex ? '2px solid #6366f1' : '2px solid transparent'
                                            }}
                                        >
                                            <span style={{color: '#fff', fontSize: '12px'}}>Scene {clip.scene_number}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            
                            {/* Assemble button */}
                            <div style={{marginTop: '30px', textAlign: 'center'}}>
                                <button 
                                    className="btn-primary"
                                    onClick={assembleFinalVideo}
                                    disabled={isAssemblingVideo || regeneratingClip !== null}
                                    style={{padding: '15px 40px', fontSize: '1.1rem'}}
                                >
                                    {isAssemblingVideo ? '🎬 Assembling...' : '🎬 Assemble Final Video'}
                                </button>
                                <p style={{color: '#888', marginTop: '10px', fontSize: '0.9rem'}}>
                                    This will combine all scenes and add background music
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="loading-state">
                            <div className="spinner"></div>
                            <p>Loading scene clips...</p>
                        </div>
                    )}
                </div>
            )}

            {/* Stage 7: Video */}
            {stage === 'video' && (
                <div className="stage-content">
                    <h2>Step 7: Final Video</h2>
                    {isGeneratingVideo || isAssemblingVideo ? (
                        <div className="loading-state">
                            <div className="spinner"></div>
                            <p>{isAssemblingVideo ? 'Assembling final video with background music...' : 'Creating video...'}</p>
                            <p className="status-text">Status: {videoStatus}</p>
                        </div>
                    ) : videoPath ? (
                        <>
                            <div className="video-preview">
                                <video controls width="100%">
                                    <source src={`${API_URL}${videoPath}`} type="video/mp4" />
                                </video>
                            </div>
                        </>
                    ) : null}
                </div>
            )}

            {/* Stage 8: Metadata */}
            {stage === 'metadata' && metadata && (
                <div className="stage-content">
                    <h2>Step 8: YouTube Metadata</h2>
                    <div className="metadata-display">
                        <div className="metadata-field">
                            <label>Title:</label>
                            <input 
                                type="text" 
                                value={metadata.youtube_title} 
                                onChange={(e) => setMetadata({...metadata, youtube_title: e.target.value})}
                            />
                        </div>
                        <div className="metadata-field">
                            <label>Description:</label>
                            <textarea 
                                value={metadata.youtube_description}
                                onChange={(e) => setMetadata({...metadata, youtube_description: e.target.value})}
                                rows={8}
                            />
                        </div>
                        <div className="metadata-field">
                            <label>Tags:</label>
                            <p>{metadata.tags ? metadata.tags.join(', ') : ''}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Stage 9: Preview */}
            {stage === 'preview' && youtubeUrl && (
                <div className="stage-content">
                    <h2>✅ Story Published!</h2>
                    <div className="success-message">
                        <p>Your story video has been successfully uploaded to YouTube!</p>
                        <a href={youtubeUrl} target="_blank" rel="noopener noreferrer" className="youtube-link">
                            🎬 View on YouTube
                        </a>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Storyteller;
